/**
 * Status, severity and coverage — the small pieces that repeat on every screen.
 *
 * All render through `StatusBadge`, so the app stays one visual system. Status
 * is never carried by colour alone: every badge prints a word.
 *
 * `file.declaredAs` is the file's kind, set by the app from its classification
 * counts (UNKNOWN until a file is classified, and for H2R). The four
 * `classified*` counters cover SALE, RETURN, ADJUSTMENT and UNKNOWN only.
 * A CANCELLATION count will not be drawn — `theme/base.css` has no
 * `.cb-cancellation` rule and that stylesheet is vendored.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { MetricCard, StatusBadge } from '../../shared/ui';
import { count, moneyShort, percent } from '../../utils/format';
import type {
	Classification,
	DeclaredType,
	FileRecord,
	Severity,
} from '../../schemas';

/* ═══ severity ═════════════════════════════════════════════════════════════ */

export function SeverityBadge({ value }: { value: Severity }) {
	return <StatusBadge group="severity" value={value} />;
}

/* No primitive badge: which of seven shapes an analysis is is true but not
   actionable, and it cost a column on the one table auditors scan. The
   `primitive` field stays on the data — `AnalysisResultTable` reads it. */

/* ═══ declared vs detected ═════════════════════════════════════════════════
   The single most important cell in the files table. What the uploader SAID
   the file is, beside what the ROWS turned out to be — and a word when they
   disagree, because a silent correction is how a returns file becomes a sales
   figure.                                                                  */

const DECLARED_LABEL: Record<DeclaredType, string> = {
	SALES: 'Sales',
	RETURNS: 'Returns',
	BOTH: 'Sales + Returns',
	UNKNOWN: 'Unknown',
};

/* An H2R file is never declared, classified or classed: its figure is the row
   count, so none of the Sales/Returns words below apply to it. */
const isH2RFile = (file: FileRecord) => file.module === 'H2R';

export function declaredLabel(value: DeclaredType | null): string {
	return value ? DECLARED_LABEL[value] : '—';
}

/** The three classified counters `detectedShape`/`presentClasses` reason
 *  over — see the file header for why there are only three. */
function classifiedCounts(file: FileRecord): {
	SALE: number;
	RETURN: number;
	ADJUSTMENT: number;
} {
	return {
		SALE: file.classifiedSales ?? 0,
		RETURN: file.classifiedReturns ?? 0,
		ADJUSTMENT: file.classifiedAdjust ?? 0,
	};
}

/** What the row content says this file actually holds. */
export function detectedShape(
	file: FileRecord,
): DeclaredType | 'ADJUSTMENTS' | 'EMPTY' {
	const { SALE, RETURN, ADJUSTMENT } = classifiedCounts(file);
	if (SALE + RETURN + ADJUSTMENT === 0) return 'EMPTY';
	if (ADJUSTMENT > SALE && ADJUSTMENT > RETURN) return 'ADJUSTMENTS';
	if (SALE > 0 && RETURN > 0) return 'BOTH';
	if (RETURN > 0) return 'RETURNS';
	return 'SALES';
}

/**
 * Everything the file actually holds, in words. A share threshold, not a bare
 * non-zero test: nearly every register carries a few rate-difference rows, and
 * 1% is the firm ruleset's own materiality for a population.
 */
const MATERIAL_SHARE = 0.01;

function presentClasses(
	file: FileRecord,
): Array<'SALE' | 'RETURN' | 'ADJUSTMENT'> {
	const counts = classifiedCounts(file);
	const total = counts.SALE + counts.RETURN + counts.ADJUSTMENT;
	if (total === 0) return [];
	return (['SALE', 'RETURN', 'ADJUSTMENT'] as const).filter(
		(key) => counts[key] / total >= MATERIAL_SHARE,
	);
}

const CLASS_WORD: Record<'SALE' | 'RETURN' | 'ADJUSTMENT', string> = {
	SALE: 'sales',
	RETURN: 'returns',
	ADJUSTMENT: 'adjustments',
};

export function detectedLabel(file: FileRecord): string {
	if (isH2RFile(file)) return `${count(file.rowCount ?? 0)} employees`;
	const present = presentClasses(file);
	if (present.length === 0) return 'Nothing read';
	const words = present.map((key) => CLASS_WORD[key]);
	const joined = words.join(' + ');
	return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/**
 * True when the file holds a population its declaration does not admit —
 * counting rate differences as goods coming back overstates the return rate.
 * UNKNOWN admits everything, which is why it is the honest default.
 */
export function conflicts(file: FileRecord): boolean {
	if (isH2RFile(file)) return false;
	if (!file.declaredAs || file.declaredAs === 'UNKNOWN') return false;
	const present = presentClasses(file);
	if (present.length === 0) return false;
	const admitted: Record<Exclude<DeclaredType, 'UNKNOWN'>, string[]> = {
		SALES: ['SALE'],
		RETURNS: ['RETURN'],
		BOTH: ['SALE', 'RETURN', 'ADJUSTMENT'],
	};
	const allowed = admitted[file.declaredAs];
	return present.some((key) => !allowed.includes(key));
}

/* ═══ figures ══════════════════════════════════════════════════════════════
   Number cards: one card per figure, side by side. The dashboard's `Stat`
   draws the same card, so every number in the app looks alike.            */

export function Figures({ children }: { children: ReactNode }) {
	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
				gap: 'var(--s3)',
			}}
		>
			{children}
		</Box>
	);
}

export function Figure({
	to,
	...card
}: Parameters<typeof MetricCard>[0] & {
	/** Makes the whole card a link. */
	to?: string;
}) {
	if (to) {
		return (
			<Box
				component={Link}
				to={to}
				sx={{
					display: 'block',
					color: 'inherit',
					textDecoration: 'none',
					'&:hover > div': {
						borderColor: 'var(--line-strong)',
						boxShadow: 'var(--e2)',
						transform: 'translateY(-1px)',
					},
				}}
			>
				<MetricCard {...card} />
			</Box>
		);
	}
	return <MetricCard {...card} />;
}

/* ═══ classification split ═════════════════════════════════════════════════ */

/** Every member of `Classification`, in reading order. `CANCELLATION` used to
 *  be excluded here because `theme/base.css` has no `.cb-cancellation`
 *  rule; the vocabulary no longer has that member at all, so the list is now
 *  complete rather than deliberately short. */
const CLASS_ORDER: Classification[] = [
	'SALES',
	'RETURNS',
	'ADJUSTMENT',
	'UNKNOWN',
	'EXCLUDED',
];

/** The CSS suffix each member is drawn with. Not `key.toLowerCase()`: the
 *  vendored theme styles `.cb-sale`/`.cb-return`, singular, so deriving the
 *  class would render colourless slivers. */
const CLASS_CSS: Record<Classification, string> = {
	SALES: 'sale',
	RETURNS: 'return',
	ADJUSTMENT: 'adjustment',
	UNKNOWN: 'unknown',
	EXCLUDED: 'excluded',
};

/** The split of rows by classification: one stacked bar and a legend. */
export function ClassificationSplit({
	rows,
	total,
}: {
	rows: Record<Classification, number>;
	total: number;
	/** Kept for callers; the bar is the only form. */
	compact?: boolean;
}) {
	const present = CLASS_ORDER.filter((key) => rows[key] > 0);
	return (
		<div className="stack">
			<div
				className="classbar"
				role="img"
				aria-label="Rows by classification"
			>
				{present.map((key) => (
					<span
						key={key}
						className={`cb-${CLASS_CSS[key]}`}
						style={{
							width: `${total > 0 ? (rows[key] / total) * 100 : 0}%`,
						}}
					/>
				))}
			</div>
			<div className="row wrapped" style={{ gap: 'var(--s3)' }}>
				{present.map((key) => (
					<span
						key={key}
						className="row"
						style={{ gap: 'var(--s2)' }}
					>
						<StatusBadge group="classification" value={key} />
						<span>
							<b>{count(rows[key])}</b>{' '}
							<span className="mut">
								{total > 0
									? percent((rows[key] / total) * 100, 1)
									: '—'}
							</span>
						</span>
					</span>
				))}
			</div>
		</div>
	);
}

/** ₹97.17 Cr, with the exact figure on hover. */
export function Money({ value }: { value: number | null }) {
	if (value === null) return <span className="mut">—</span>;
	return (
		<span title={`₹${value.toLocaleString('en-IN')}`}>
			{moneyShort(value)}
		</span>
	);
}

/* ═══ kinds ════════════════════════════════════════════════════════════════
   What a file holds — Sales / Returns / Sales + Returns — as a label and a
   colour (Client Documents).                                               */

export const KINDS: Array<{
	value: DeclaredType;
	label: string;
	colour: string;
}> = [
	{ value: 'SALES', label: 'Sales', colour: 'var(--class-sales)' },
	{ value: 'RETURNS', label: 'Returns', colour: 'var(--class-returns)' },
	{
		value: 'BOTH',
		label: 'Sales + Returns',
		colour: 'var(--class-unclassified)',
	},
	{ value: 'UNKNOWN', label: '-', colour: 'var(--class-adjustments)' },
];

export const kindOf = (file: FileRecord): DeclaredType => file.declaredAs;
export const kindLook = (file: FileRecord) =>
	KINDS.find((kind) => kind.value === kindOf(file)) ??
	KINDS[KINDS.length - 1]!;

export function KindLabel({ file }: { file: FileRecord }) {
	const look = kindLook(file);
	return (
		<Box
			component="span"
			sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
		>
			<Typography
				component="span"
				sx={{ fontSize: 'inherit', color: look.colour }}
			>
				{look.label}
			</Typography>
		</Box>
	);
}
